import localFont from 'next/font/local';
import { Typography, TypographyProps } from '@eventbrite/marmalade';
import styles from './GradientTypography.module.scss';
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

const foundersGroteskRegular = localFont({
    src: '../fonts/founders-grotesk-r-bold.woff2',
    display: 'swap',
});
interface GradientTypographyProps extends TypographyProps {
    fontSize?: string;
    startColor?: string;
    endColor?: string;
}
export const GradientTypography = ({
    startColor,
    endColor,
    fontSize,
    variant = 'display-sm',
    color = 'neutral-1000',
    ...props
}: GradientTypographyProps) => {
    const titleRef = useRef<HTMLDivElement>(null);
    const [titleLines, setTitleLines] = useState<string[]>([]);

    useEffect(() => {
        if (titleRef.current) {
            const element = titleRef.current;
            const containerWidth = element.offsetWidth;
            const words = (props.children || '').toString().split(' ');
            const lines: string[] = [];
            let currentLine = '';

            // Create a temporary canvas to measure text width
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!context) return;

            // Get the computed style to match font properties
            const computedStyle = window.getComputedStyle(element);
            context.font = `${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;

            const bufferWidth = 25; // 25px buffer for padding
            const effectiveWidth = containerWidth - bufferWidth;

            words.forEach((word) => {
                const testText = currentLine + (currentLine ? ' ' : '') + word;
                const textWidth = context.measureText(testText).width;
                const ratio = textWidth / effectiveWidth;

                if (ratio > 0.8 && currentLine) {
                    lines.push(currentLine.trim());
                    currentLine = word;
                } else {
                    currentLine = currentLine + (currentLine ? ' ' : '') + word;
                }
            });

            if (currentLine) {
                lines.push(currentLine.trim());
            }

            setTitleLines(lines);
        }
    }, [props.children]);

    return (
        <Typography
            {...props}
            style={
                {
                    fontFamily: foundersGroteskRegular.style.fontFamily,
                    fontSize,
                    letterSpacing: '0.01em',
                    lineHeight: '1.1em',
                    fontWeight: 'unset',
                    '--TypographyColor': '#13002d',
                } as React.CSSProperties
            }
            variant={variant}
            color={color}
            className={cn(styles.gradientText, props.className)}
        >
            <span ref={titleRef} className={styles.titleMeasure}>
                {titleLines.map((line, index) => (
                    <span
                        key={index}
                        className={styles.titleLine}
                        style={
                            {
                                '--GradientStart': index % 2 === 1 ? startColor : endColor,
                                '--GradientEnd': index % 2 === 1 ? endColor : startColor,
                            } as React.CSSProperties
                        }
                    >
                        {line}
                    </span>
                ))}
            </span>
        </Typography>
    );
};
